/**
 * Глобальний toast-канал (Sprint 8).
 *
 * Чому без react-hot-toast / sonner:
 * - PoC, мінімум deps;
 * - react-hot-toast тягне ~12KB gzip + власні Provider-и;
 * - наш use-case вузький: показати 403/5xx-помилку, повідомлення про
 *   успішний логін. 50 LOC + tailwind клас — вистачить.
 *
 * Як використовувати:
 *   import { toast } from "@/store/toast";
 *   toast.error("Немає прав");
 *   toast.success("Збережено");
 *
 * Контейнер-компонент <ToastViewport /> рендериться один раз у App.tsx —
 * додамо при першому UI-компоненті (наступний крок Sprint 8).
 */
import { create } from "zustand";

export type ToastKind = "info" | "success" | "warning" | "error";

export interface ToastMessage {
  id: number;
  kind: ToastKind;
  text: string;
  /** Мілісекунди до автодисмісу. 0 = не автодисмісити. */
  ttl: number;
  createdAt: number;
  /** Кнопка в тості — напр. «Скасувати» останню дію (власник 26.09.2026). */
  action?: ToastAction;
}

export interface ToastAction {
  label: string;
  run: () => void | Promise<void>;
}

interface ToastState {
  items: ToastMessage[];
  push: (kind: ToastKind, text: string, ttl?: number, action?: ToastAction) => number;
  dismiss: (id: number) => void;
  clear: () => void;
}

let _nextId = 1;

export const useToastStore = create<ToastState>((set, get) => ({
  items: [],

  // Помилки висять довше — їх треба встигнути прочитати.
  push: (kind, text, ttl, action) => {
    // Той самий текст двічі поспіль — це одна подія, а не дві. Тепер, коли
    // перехоплювач говорить і на 422, екран, який ловить ту саму помилку й
    // показує її сам, ставив би дві однакові смужки одну на одну.
    const dup = get().items.find(
      (m) => m.text === text && m.kind === kind && Date.now() - m.createdAt < 2000,
    );
    if (dup) return dup.id;

    if (ttl === undefined) ttl = kind === "error" ? 8000 : 6000;
    const id = _nextId++;
    const msg: ToastMessage = { id, kind, text, ttl, createdAt: Date.now(), action };
    set((s) => ({ items: [...s.items, msg] }));
    if (ttl > 0) {
      // setTimeout у store — нормальний pattern для self-disposing toasts.
      // Edge-case: якщо користувач вручну закрив раніше — dismiss() стане no-op.
      setTimeout(() => get().dismiss(id), ttl);
    }
    return id;
  },

  dismiss: (id) => {
    set((s) => ({ items: s.items.filter((m) => m.id !== id) }));
  },

  clear: () => set({ items: [] }),
}));

/** Зручний sugar для виклику з не-React коду (axios interceptors). */
export const toast = {
  info: (text: string, ttl?: number) =>
    useToastStore.getState().push("info", text, ttl),
  success: (text: string, ttl?: number) =>
    useToastStore.getState().push("success", text, ttl),
  warning: (text: string, ttl?: number) =>
    useToastStore.getState().push("warning", text, ttl),
  error: (text: string, ttl?: number) =>
    useToastStore.getState().push("error", text, ttl),
};
